import React from 'react';
import styles from './WidgetHeader.module.css';
import { ExpandIcon, MinimizeIcon, CloseIcon, MenuIcon } from './Icons'; // Assuming Icons are in the same common directory
import useAuthStore from '../../store/authStore'; // Import useAuthStore
import useMediaQuery from '../../hooks/useMediaQuery'; // Import useMediaQuery

export interface WidgetHeaderProps {
  chatViewMode: 'sidebar' | 'fullscreen' | 'collapsed'; // Add 'collapsed' for completeness, though header isn't usually shown then
  isHistoryPanelVisibleOnMobile?: boolean; // Optional as it's only relevant in fullscreen
  brandText: string;
  onClose: () => void;
  onExpandOrMinimize: () => void;
  onToggleHistoryPanel?: () => void; // Optional as it's only relevant in fullscreen
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  chatViewMode,
  isHistoryPanelVisibleOnMobile,
  brandText,
  onClose,
  onExpandOrMinimize,
  onToggleHistoryPanel,
}) => {
  // Get messageLimits and userStatus from the store
  const messageLimits = useAuthStore((state) => state.messageLimits);
  const userStatus = useAuthStore((state) => state.userStatus);

  // For the new mobile sidebar hamburger menu
  const isMobile = useMediaQuery('(max-width: 768px)');
  // We'll use the passed 'chatViewMode' prop and 'onToggleHistoryPanel' prop.
  // 'isHistoryPanelVisibleOnMobile' prop is used for the title/aria-label of the existing fullscreen button.

  // Calculate remaining messages with more robust null checks
  const remainingMessages = 
    messageLimits && 
    typeof messageLimits.max === 'number' && 
    typeof messageLimits.current === 'number'
      ? Math.max(0, messageLimits.max - messageLimits.current)
      : null;

  // Only display message limits if user status is known and we have valid messageLimits data
  const displayMessageLimits = 
    userStatus !== 'unknown' && 
    remainingMessages !== null && 
    messageLimits?.max !== null;

  return (
    <header className={styles.header}>
      {/* New Hamburger Menu for Sidebar on Mobile */}
      {isMobile && chatViewMode === 'sidebar' && onToggleHistoryPanel && (
        <button
          onClick={onToggleHistoryPanel}
          className={`${styles.iconButton} ${styles.menuButton}`} // Reuse existing styles
          title={isHistoryPanelVisibleOnMobile ? "Ascunde istoricul conversațiilor" : "Arată istoricul conversațiilor"}
          aria-label={isHistoryPanelVisibleOnMobile ? "Ascunde panoul istoricului conversațiilor" : "Arată panoul istoricului conversațiilor"}
        >
          <MenuIcon className={styles.icon} title="Pictogramă meniu" />
        </button>
      )}

      {/* Existing Hamburger Menu for Fullscreen (any device) */}
      {chatViewMode === 'fullscreen' && onToggleHistoryPanel && (
        <button
          onClick={onToggleHistoryPanel}
          className={`${styles.iconButton} ${styles.menuButton}`}
          title={isHistoryPanelVisibleOnMobile ? "Ascunde istoricul conversațiilor" : "Arată istoricul conversațiilor"}
          aria-label={isHistoryPanelVisibleOnMobile ? "Ascunde panoul istoricului conversațiilor" : "Arată panoul istoricului conversațiilor"}
        >
          <MenuIcon className={styles.icon} title="Pictogramă meniu" />
        </button>
      )}
      <div className={styles.brandContainer}>
        <span className={styles.brand}>{brandText}</span>
        {displayMessageLimits && messageLimits && (
          <span className={styles.messageLimits}>
            {remainingMessages}/{messageLimits.max} mesaje rămase
          </span>
        )}
      </div>
      <div className={styles.iconButtonContainer}>
        {(chatViewMode === 'sidebar' || chatViewMode === 'fullscreen') && (
          <button
            onClick={onExpandOrMinimize}
            className={styles.iconButton}
            title={chatViewMode === 'fullscreen' ? "Minimizează chat" : "Extinde la ecran complet"}
            aria-label={chatViewMode === 'fullscreen' ? "Minimizează chat" : "Extinde la vizualizarea pe ecran complet"}
          >
            {chatViewMode === 'fullscreen' ? (
              <MinimizeIcon className={styles.icon} title="Pictogramă minimizare" />
            ) : (
              <ExpandIcon className={styles.icon} title="Pictogramă extindere" />
            )}
          </button>
        )}
        <button
          onClick={onClose}
          className={styles.iconButton}
          title="Închide chat"
          aria-label="Închide chat"
        >
          <CloseIcon className={styles.icon} title="Pictogramă închidere" />
        </button>
      </div>
    </header>
  );
};

export default WidgetHeader; 